<template>
  <section class="mobile-journal" aria-label="Мобільний журнал">
    <div class="mobile-journal__student-picker">
      <label for="mobile-journal-student">Учень</label>
      <select id="mobile-journal-student" v-model.number="selectedRowIndex">
        <option
          v-for="option in studentOptions"
          :key="option.rowIndex"
          :value="option.rowIndex"
        >
          {{ option.name }}
        </option>
      </select>
    </div>

    <div v-if="selectedRow" class="mobile-journal__student">
      <span class="mobile-journal__avatar">{{ studentInitials }}</span>
      <div>
        <strong>{{ selectedRow[1].label }}</strong>
        <span>{{ gradeItems.length }} стовпчиків у поточному діапазоні</span>
      </div>
    </div>

    <div v-if="selectedRow" class="mobile-journal__grades">
      <article
        v-for="grade in gradeItems"
        :key="grade.indexColumn"
        class="mobile-journal__grade"
        :class="{
          'mobile-journal__grade--selected': isInRange(grade.indexColumn),
        }"
      >
        <button
          type="button"
          class="mobile-journal__lesson"
          :title="grade.header.lessonName"
          @click="$emit('select-column', grade.indexColumn)"
        >
          <span>{{ grade.header.label }}</span>
          <small>{{ grade.header.lessonName || 'Навчальний стовпчик' }}</small>
        </button>

        <div class="mobile-journal__value">
          <input
            v-if="grade.cell.edit"
            :value="grade.cell.label"
            type="text"
            inputmode="numeric"
            aria-label="Редагування оцінки"
            @input="$emit('update-cell', grade.cell, $event.target.value)"
            @blur="$emit('finish-cell', grade.cell)"
            @keydown.enter.prevent="$emit('finish-cell', grade.cell)"
          />
          <button
            v-else
            type="button"
            :class="{ 'mobile-journal__empty': grade.cell.label === '-' }"
            @click="
              $emit('open-cell', {
                item: grade.cell,
                indexRow: selectedRowIndex,
                indexColumn: grade.indexColumn,
              })
            "
          >
            {{ grade.cell.label || '—' }}
          </button>
        </div>
      </article>
    </div>

    <div v-else class="mobile-journal__empty-state">
      У журналі поки немає учнів.
    </div>
  </section>
</template>

<script>
export default {
  name: 'JournalMobileView',
  props: {
    rows: { type: Array, default: () => [] },
    isInRange: { type: Function, required: true },
  },
  data: () => ({
    selectedRowIndex: 1,
  }),
  computed: {
    headers() {
      return this.rows[0] || [];
    },
    studentOptions() {
      return this.rows.slice(1).map((row, index) => ({
        rowIndex: index + 1,
        name: row[1] ? row[1].label : `Учень ${index + 1}`,
      }));
    },
    selectedRow() {
      return this.rows[this.selectedRowIndex] || null;
    },
    studentInitials() {
      if (!this.selectedRow || !this.selectedRow[1]) return '?';
      return String(this.selectedRow[1].label || '')
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0))
        .join('')
        .toUpperCase();
    },
    gradeItems() {
      if (!this.selectedRow) return [];
      return this.selectedRow.slice(2).map((cell, index) => {
        const indexColumn = index + 2;
        return {
          cell,
          header: this.headers[indexColumn] || {},
          indexColumn,
        };
      });
    },
  },
  watch: {
    rows() {
      if (!this.rows[this.selectedRowIndex]) this.selectedRowIndex = 1;
    },
  },
};
</script>

<style lang="less" scoped>
.mobile-journal {
  display: grid;
  gap: 12px;
}
.mobile-journal__student-picker {
  display: grid;
  gap: 6px;
}
.mobile-journal__student-picker label {
  color: #526174;
  font-size: 12px;
  font-weight: 800;
}
.mobile-journal__student-picker select {
  background: #fff;
  border: 1px solid #cfd8d4;
  border-radius: 10px;
  color: #172033;
  font: inherit;
  height: 44px;
  padding: 0 11px;
  width: 100%;
}
.mobile-journal__student {
  align-items: center;
  background: var(--theme-surface-decorative);
  border: 1px solid var(--theme-border-decorative);
  border-radius: 12px;
  display: flex;
  gap: 10px;
  padding: 11px;
}
.mobile-journal__avatar {
  align-items: center;
  background: var(--theme-banner-background);
  border-radius: 10px;
  color: var(--theme-header-background);
  display: flex;
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 900;
  height: 38px;
  justify-content: center;
  width: 38px;
}
.mobile-journal__student > div > strong,
.mobile-journal__student > div > span {
  display: block;
}
.mobile-journal__student > div > strong {
  color: #172033;
  font-size: 14px;
}
.mobile-journal__student > div > span {
  color: #718095;
  font-size: 11px;
  margin-top: 2px;
}
.mobile-journal__grades {
  display: grid;
  gap: 7px;
}
.mobile-journal__grade {
  align-items: center;
  background: #fff;
  border: 1px solid #e0e6e9;
  border-radius: 11px;
  display: flex;
  min-height: 60px;
  overflow: hidden;
}
.mobile-journal__grade--selected {
  border-color: var(--theme-border-decorative);
  box-shadow: inset 3px 0 var(--theme-header-background);
}
.mobile-journal__lesson {
  background: transparent;
  border: 0;
  color: #263448;
  cursor: pointer;
  display: block;
  flex: 1 1 auto;
  min-width: 0;
  padding: 10px 12px;
  text-align: left;
}
.mobile-journal__lesson span,
.mobile-journal__lesson small {
  display: block;
}
.mobile-journal__lesson span {
  font-size: 13px;
  font-weight: 800;
}
.mobile-journal__lesson small {
  color: #718095;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.mobile-journal__value {
  align-items: center;
  align-self: stretch;
  border-left: 1px solid #e5e9ec;
  display: flex;
  flex: 0 0 64px;
  justify-content: center;
}
.mobile-journal__value button,
.mobile-journal__value input {
  background: transparent;
  border: 0;
  color: #172033;
  font: inherit;
  font-size: 15px;
  font-weight: 800;
  height: 100%;
  outline: none;
  text-align: center;
  width: 100%;
}
.mobile-journal__value input {
  background: var(--theme-banner-background);
  box-shadow: inset 0 0 0 2px var(--theme-header-background);
}
.mobile-journal__value .mobile-journal__empty {
  color: #a3acb8;
  font-weight: 500;
}
.mobile-journal__empty-state {
  color: #718095;
  padding: 24px;
  text-align: center;
}
</style>
